import { useCallback, useEffect, useMemo, useReducer } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { t } from "i18next";
import TabsUI, { TabItem } from "@/shared/components/atoms/TabsUI";
import { Permission } from "@/shared/hooks/usePermission";
import { selectUser } from "@/app/auth/auth.slice";
import {
  useCatchmentQuery,
  useCatchmentExportMutation,
} from "@/app/catchment/catchment.api";
import {
  useGetBranchByCompanyQuery,
  useGetCampaignsQuery,
  useGetLeadCaptionQuery,
  useGetTagsMarketingQuery,
} from "@/app/leads/leads.api";
import { CreateUserModal } from "../InformationUserPages/Components/CreateUser";
import CatchmentCampaignPage from "./components/CatchmentCampaignPage";
import { CatchmentDirectCenterPage } from "./components/CatchmentDirectCenterPage";
import { pageReducer, initialState } from "./reducers/pageReducer";
import { useDebounce } from "@/shared/hooks/debounce";
import {
  useCountriesQuery,
  useGetAllCountriesQuery,
} from "@/app/app.apiCountry";
import { useStatesQuery, useTimezonesQuery } from "@/app/app.apiStates";
import { useFormik } from "formik";
import { useFetchGymsMutation, useGymsQuery } from "@/app/gyms/gyms.api";
import { useCreateNewClientCaptionMutation } from "@/app/users/users.api";

const tabs: TabItem[] = [
  {
    id: "campaigns",
    name: t("recovery.views.tab_1_catchment"),
    permission: Permission.CATCHMENT_CAMPAIGNS,
  },
  {
    id: "direct",
    name: t("recovery.views.tab_2_catchment"),
    permission: Permission.CATCHMENT_DIRECT,
  },
];

export const CatchmentPage = () => {
  const [state, dispatch] = useReducer(pageReducer, initialState);
  const { id: branchID } = useParams();
  const branchIdUser = useSelector(selectUser)?.branch?.id;
  const currentUser = useSelector(selectUser);
  console.log({ currentUser });
  const debouncedSearch = useDebounce(state.formValues.search, 500);
  const [createUserCaption] = useCreateNewClientCaptionMutation();

  // Queries
  const debouncedFormValues = useMemo(
    () => ({
      ...state.formValues,
      search: debouncedSearch,
    }),
    [state.formValues, debouncedSearch]
  );

  const catchmentQuery = useCatchmentQuery(debouncedFormValues);
  const [catchmentExport] = useCatchmentExportMutation();

  // Memoized branch filter
  const branchFilter = useMemo(() => {
    return state.formValues.branch_filter || branchIdUser;
  }, [state.formValues.branch_filter, branchIdUser]);

  // Branch company query
  const branchByCompanyQuery = useGetBranchByCompanyQuery(
    {
      branch_filter: branchFilter,
    },
    { skip: !branchFilter }
  );

  const countries = useCountriesQuery({});
  const statesQuery = useStatesQuery(undefined);
  const [gyms, { data }] = useFetchGymsMutation();

  useEffect(() => {
    gyms({
      limit: 1000,
    }).unwrap();
  }, []);

  // Lead caption query
  const leadCaptionQuery = useGetLeadCaptionQuery(
    {
      branch_filter: branchFilter,
      sex_filter: debouncedFormValues.sex_filter,
      stages_filter: debouncedFormValues.stages_filter,
      campaign_filter: debouncedFormValues.campaign_filter,
      is_in_campaign_filter: debouncedFormValues.is_in_campaign_filter,
      users_tasks_planned: debouncedFormValues.users_tasks_planned,
      max_lead: 1000,
      search: debouncedFormValues.search,
    },
    { skip: !branchFilter }
  );

  const campaignsQuery = useGetCampaignsQuery({
    skip: !branchFilter,
  });
  const tagsMarketingQuery = useGetTagsMarketingQuery({
    skip: !branchFilter,
  });

  // Memoized options
  const optionsTagsMarketing = useMemo(() => {
    if (!catchmentQuery?.data?.tags_marketing) return [];
    return tagsMarketingQuery.data?.map((tag: any) => ({
      id: tag.id,
      name: tag.name,
    }));
  }, [catchmentQuery?.data?.tags_marketing]);

  const optionsBranches = useMemo(() => {
    if (!branchByCompanyQuery?.data?.length) return [];

    return branchByCompanyQuery.data?.map((branch: any) => ({
      id: branch.id,
      name: branch.branch,
    }));
  }, [branchByCompanyQuery.data]);

  const optionsCampaigns = useMemo(() => {
    return campaignsQuery.data?.map((campaign: any) => ({
      id: campaign.id,
      name: campaign.name,
    }));
  }, [campaignsQuery.data]);

  // Effects
  useEffect(() => {
    if (branchFilter) {
      const selectedBranch = optionsBranches?.find(
        (branch: any) => branch.id === branchFilter
      );
      if (selectedBranch) {
        dispatch({ type: "SET_SELECTED_BRANCH", branch: selectedBranch });
      }
    }
  }, [branchFilter, optionsBranches]);

  // Handlers
  const handleExport = useCallback(async () => {
    try {
      const response = await catchmentExport(debouncedFormValues).unwrap();
      const blob = new Blob([response], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "users.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting catchment:", error);
    }
  }, [catchmentExport, debouncedFormValues]);

  const handleCreateUser = useCallback((user: any) => {
    dispatch({ type: "SET_PERSON_REGISTER", person: user });
    dispatch({ type: "TOGGLE_USER_MODAL" });
  }, []);

  const handleTabChange = useCallback((tabId: string) => {
    dispatch({ type: "CHANGE_TAB", tabId });
  }, []);

  const handleBranchSelect = useCallback((branch: any) => {
    dispatch({ type: "SET_SELECTED_BRANCH", branch });
  }, []);

  const handleCloseUserModal = useCallback(() => {
    dispatch({ type: "TOGGLE_USER_MODAL" });
  }, []);

  const isLoading = leadCaptionQuery.isLoading || leadCaptionQuery.isFetching;
  const people = useMemo(() => {
    const dataEmpty =
      !leadCaptionQuery.data ||
      !campaignsQuery.data ||
      !tagsMarketingQuery.data;

    if (dataEmpty) {
      return [];
    }

    return leadCaptionQuery.data.map((lead: any) => ({
      ...lead,
      campaign: campaignsQuery.data.find(
        (campaign: any) => campaign.id === lead.campaign_id
      )?.name,
      labels: tagsMarketingQuery?.data
        ?.filter((tag: any) => +tag.id === +lead.tag_merketing)
        .map((item) => item.name),
      dateTask: lead?.last_lead?.date ?? undefined,
      task: lead?.last_lead?.task ?? undefined,
    }));
  }, [leadCaptionQuery.data, campaignsQuery.data, tagsMarketingQuery.data]);

  const gymOptions = useMemo(() => {
    if (!data?.data) return [];

    return data?.data?.map((gym) => ({
      id: gym.id,
      name: gym.gym,
    }));
  }, [data?.data]);

  const handleCreateCaptionUser = async (values) => {
    try {
      const finalData = {
        ...values,
      };
      console.log({ finalData });
      //createUserCaption({userData: finalData});
      //handleCloseUserModal
      //dispatch({ type: "SET_PERSON_REGISTER", person: {} });
    } catch (error) {
      console.log("error on handleCreateCaptionUser", error);
      throw error;
    }
  };

  return (
    <div className="flex-1 overflow-hidden">
      <div className="mt-1">
        <div className="px-4 py-5">
          <TabsUI
            tabs={tabs}
            handleChange={handleTabChange}
            current={state.formValues.catchment_page}
          />
        </div>

        {state.formValues.catchment_page === "campaigns" && (
          <CatchmentCampaignPage
            branchId={branchID}
            companies={gymOptions}
            selectBranch={handleBranchSelect}
            selectedBranch={state.selectedBranch}
            selectedCompany={state.selectedCompany}
            createUser={handleCreateUser}
            isLoading={isLoading}
            isError={catchmentQuery.isError}
            people={people}
            branches={optionsBranches}
            tagsMarketing={optionsTagsMarketing}
            campaigns={optionsCampaigns}
            exportData={handleExport}
            formValues={state.formValues}
            dispatch={dispatch}
          />
        )}

        {state.formValues.catchment_page === "direct" && (
          <CatchmentDirectCenterPage
            createUser={handleCreateUser}
            isLoading={isLoading}
            isError={catchmentQuery.isError}
            people={people}
            branchId={branchIdUser}
            branches={optionsBranches}
            tagsMarketing={optionsTagsMarketing}
            campaigns={optionsCampaigns}
            exportData={handleExport}
            formValues={state.formValues}
            dispatch={dispatch}
            selectBranch={handleBranchSelect}
            selectedBranch={state.selectedBranch}
            isDrawerOpen={state.isDrawerOpen}
            isKpisOpen={state.isKpisOpen}
            isCreateModalOpen={state.isCreateModalOpen}
            refetchPeople={leadCaptionQuery.refetch}
          />
        )}
      </div>

      <CreateUserModal
        open={state.openUserModal}
        setOpen={handleCloseUserModal}
        defaultUser={state.selectPersonRegister}
        currentUserFromStore={currentUser}
        onCreateUser={handleCreateCaptionUser}
        isLoadingCreate={false}
        countriesData={countries?.data ?? []}
        statesData={statesQuery?.data ?? []}
        gymsData={gymOptions}
        defaultGymId={currentUser?.branch?.gymId ?? ""}
      />
    </div>
  );
};
